import { useCalendar } from 'components/providers/CalendarProvider'

import { isSameDay, isToday } from 'date-fns'
import dayjs, { Dayjs } from 'dayjs'
import { t } from 'i18next'
import { useEffect, useMemo } from 'react'
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from 'react-beautiful-dnd'
import { updateEventOnServer } from 'services/api/event'
import Database from 'services/db/Database'
import { localEventById, localEventByLocalId } from 'services/db/Queries/Event'
import LocalStorage from 'services/LocalStorage'
import {
  EventDoc,
  EventStatus,
  GroupDoc,
  UserSettingsDoc
} from 'types/api/changes'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import {
  buildEventsArrayForUIFromDBEventsWithRecurrentEvents,
  eventComparator
} from 'utils/api/events'
import { StartEndEvent } from 'utils/date'
import {
  eventIdForContainer,
  eventLengthInDays,
  getSupportedTimezone,
  isEventMoreThanADay,
  isEventRecurrenceValid
} from 'utils/event'
import {
  groupIdToProvider,
  isConvertedGroupId,
  ProviderType
} from 'utils/groups'
import { isGuest } from 'utils/user'

import { isEventWithinTimePeriod } from './CalendarView'
import dateToTimeString, { DayHeaderContent } from './components'
import Event from './components/Event'
import {
  DateHeader,
  EventRow,
  ListViewScrollable,
  NoItems,
  TimeColumn,
  TimeContainer,
  TimeLabel
} from './styled'
import React from 'react'
import { getDefaultTimezone } from 'index'
import { useEventPopover } from './eventPopover/EventPopoverProvider'
import { Box, CircularProgress, Typography } from '@mui/material'
import Logger from 'services/Logger'
import { CalendarRange } from 'types/components/calendar'
import { useCalendarSync } from 'components/providers/CalendarSyncProdivder'
import EventContextMenu from 'components/contextMenu/EventContextMenu'

interface ListViewProps {
  events: Model<EventDoc>[] | EventDoc[]
  dayNameFormat?: string
  userSettings?: UserSettingsDoc
  group?: Model<GroupDoc>
  groupId?: string
  groups?: GroupDoc[]
  handleAddEvent?: (startEnd: StartEndEvent) => void
  embedMode?: boolean
  loadingEvents?: boolean
}

const ListView: React.FC<ListViewProps> = React.memo(
  (props: ListViewProps) => {
    const calendar = useCalendar()
    const calendarSync = useCalendarSync()
    const { closePopover, openPopover, pointTo } = useEventPopover()

    const [eventsForListView, setEventsForListView] = React.useState<
      EventDoc[]
    >([])
    const [isProcessingEvents, setIsProcessingEvents] = React.useState(true) // Add internal loading state

    const { visibleAreaStart, visibleAreaEnd } = useMemo(() => {
      setIsProcessingEvents(true)
      setEventsForListView([])

      if (calendar.view === CalendarRange.LISTDAY) {
        return {
          visibleAreaStart: dayjs(
            LocalStorage.get(LocalStorageKey.VISIBLE_FROM)
          ).startOf('day'),
          visibleAreaEnd: dayjs(
            LocalStorage.get(LocalStorageKey.VISIBLE_FROM)
          ).endOf('day')
        }
      }

      const start = dayjs(LocalStorage.get(LocalStorageKey.VISIBLE_FROM))
        .startOf('month')
        .subtract(1, 'day')
      const end = start.add(1, 'month').add(4, 'day')
      return { visibleAreaStart: start, visibleAreaEnd: end }
    }, [
      LocalStorage.get(LocalStorageKey.VISIBLE_FROM),
      LocalStorage.get(LocalStorageKey.VISIBLE_TO)
    ])

    const [optimisticUpdates, setOptimisticUpdates] = React.useState<
      Map<string, Partial<EventDoc>>
    >(new Map())

    // Combine real events with optimistic updates
    const eventsWithOptimisticUpdates = useMemo(() => {
      return eventsForListView.map((event) => {
        const optimisticUpdate = optimisticUpdates.get(
          event._id || event.id || ''
        )
        return optimisticUpdate ? { ...event, ...optimisticUpdate } : event
      })
    }, [eventsForListView, optimisticUpdates])

    // Memoize filtered events
    const filteredEvents = useMemo(() => {
      return props.events.filter(
        (event) =>
          isEventWithinTimePeriod(event, visibleAreaStart, visibleAreaEnd) ||
          isEventRecurrenceValid(event, visibleAreaStart)
      )
    }, [props.events, visibleAreaStart, visibleAreaEnd])

    useEffect(() => {
      const eventsForListView =
        !LocalStorage.get(LocalStorageKey.VISIBLE_FROM) ||
        !LocalStorage.get(LocalStorageKey.VISIBLE_TO)
          ? []
          : buildEventsArrayForUIFromDBEventsWithRecurrentEvents(
              filteredEvents,
              visibleAreaStart,
              visibleAreaEnd
            )
      setEventsForListView(eventsForListView)
      if (
        LocalStorage.get(LocalStorageKey.VISIBLE_FROM) &&
        LocalStorage.get(LocalStorageKey.VISIBLE_TO)
      )
        setIsProcessingEvents(false) // Finish loading
    }, [filteredEvents, visibleAreaStart, visibleAreaEnd])

    // Memoize dates array
    const uniqueDates = useMemo(() => {
      let currentDate = visibleAreaStart
      const dates = []

      while (
        currentDate.isBefore(visibleAreaEnd) ||
        currentDate.isSame(visibleAreaEnd)
      ) {
        dates.push(currentDate.format('YYYY-MM-DD'))
        currentDate = currentDate.add(1, 'day')
      }

      return dates
    }, [visibleAreaStart, visibleAreaEnd])

    // Memoize events for date function
    const getEventsForDate = useMemo(() => {
      const eventsMap = new Map<string, EventDoc[]>()

      uniqueDates.forEach((date) => {
        const from = dayjs(date).startOf('day')
        const to = dayjs(date).endOf('day')
        const events = eventsWithOptimisticUpdates
          ?.filter((event) => {
            const tz =
              event.AllDay === '1'
                ? 'UTC'
                : (event.TimeZoneNameID?.length ?? 0) > 0
                  ? getSupportedTimezone(event.TimeZoneNameID)
                  : getDefaultTimezone()
            const eventStart = dayjs.unix(Number(event.StartDate)).tz(tz)
            let eventEnd = dayjs.unix(Number(event.EndDate)).tz(tz)

            if (event._id?.length === 0) {
              return isSameDay(dayjs(date).toDate(), eventStart.toDate())
            }

            if (event.late && !isToday(dayjs(date).toDate())) {
              return false
            }

            if (
              groupIdToProvider(event.GroupID ?? '') === ProviderType.GOOGLE
            ) {
              if (event.AllDay === '1') {
                eventEnd = eventEnd.add(-1, 'day')
              }
            }

            return isEventWithinTimePeriod(event, from, to)
          })
          .sort(eventComparator)

        eventsMap.set(date, events)
      })

      return (date: string) => eventsMap.get(date) || []
    }, [eventsWithOptimisticUpdates, uniqueDates, calendarSync.midnightCount])

    const onDragEnd = (result: DropResult) => {
      LocalStorage.remove(LocalStorageKey.SHOULD_KEEP_POPUP)
      setTimeout(() => {
        LocalStorage.remove(LocalStorageKey.DRAGGING)
      }, 25)

      if (!result.destination) return
      const newPositionIndex = result.destination?.index ?? 0

      const currentEvent = getEventsForDate(result.source.droppableId)[
        result.source.index
      ]

      const oldStart = currentEvent.StartDate
      const oldEnd = currentEvent.EndDate
      const oldAllday = currentEvent.AllDay
      const oldRank = currentEvent.Rank

      const startHour = dayjs(Number(currentEvent.StartDate) * 1000).hour()
      const startMinute = dayjs(Number(currentEvent.StartDate) * 1000).minute()

      let prevEvent =
        newPositionIndex === 0
          ? undefined
          : getEventsForDate(result.destination?.droppableId)[newPositionIndex]
      let nextEvent: Model<EventDoc> | EventDoc | undefined = getEventsForDate(
        result.destination?.droppableId
      )[newPositionIndex + 1]
      const duration =
        Number(currentEvent.EndDate) - Number(currentEvent.StartDate)

      if (result.destination?.droppableId !== result.source.droppableId) {
        prevEvent =
          newPositionIndex === 0
            ? undefined
            : getEventsForDate(result.destination?.droppableId)[
                newPositionIndex - 1
              ]
        nextEvent = getEventsForDate(result.destination?.droppableId)[
          newPositionIndex
        ]
      }

      let rank: string
      let newStartDate: Dayjs = dayjs(result.destination?.droppableId)
        .set('hour', startHour)
        .set('minute', startMinute)
        .set('second', 0)
      let allDay: string = currentEvent.AllDay ?? '0'

      if (prevEvent || nextEvent) {
        if (newPositionIndex === 0 && nextEvent) {
          rank = (Number(nextEvent?.Rank) - 100).toString()

          const nextDayStart = dayjs(
            Number(nextEvent.lateOriginalStartTime ?? nextEvent.StartDate) *
              1000
          )
          newStartDate = nextDayStart
          allDay = nextEvent.AllDay ?? '0'
        } else if (!nextEvent && prevEvent) {
          //we dropped to the end of the list
          rank = (Number(prevEvent?.Rank) + 100).toString()

          const prevEventStart = dayjs(
            Number(prevEvent.lateOriginalStartTime ?? prevEvent.StartDate) *
              1000
          )
          newStartDate = prevEventStart
          allDay = prevEvent.AllDay ?? '0'
        } else if (prevEvent && nextEvent) {
          if (prevEvent.StartDate === nextEvent.StartDate) {
            //we dropped inbetween
            rank = (
              (Number(nextEvent.Rank) + Number(prevEvent.Rank)) /
              2
            ).toString()
          } else {
            rank = (Number(prevEvent.Rank) + 100).toString()
          }

          const prevEventStart = dayjs(
            Number(prevEvent.lateOriginalStartTime ?? prevEvent.StartDate) *
              1000
          )
          newStartDate = prevEventStart
          allDay = prevEvent.AllDay ?? '0'
        }
      }

      const eventId = currentEvent._id || currentEvent.id || ''

      setTimeout(() => {
        if (
          LocalStorage.get(LocalStorageKey.PENDING_EVENT_DATA) !== null ||
          currentEvent._id?.length === 0
        ) {
          const newId =
            eventIdForContainer(currentEvent, newStartDate.valueOf()) ?? ''

          pointTo(document.getElementById(newId), 'draggable')
        }
      }, 50)

      if (currentEvent.someday === '1') {
        LocalStorage.set(
          LocalStorageKey.SOMEDAY_DATE,
          newStartDate.unix().toString()
        )
        LocalStorage.set(LocalStorageKey.SOMEDAY_ALL_DAY, allDay)
        return
      }

      if (LocalStorage.get(LocalStorageKey.VISIBLE_EVENT) === '') {
        LocalStorage.set(
          LocalStorageKey.VISIBLE_EVENT,
          `${currentEvent._id}${newStartDate.valueOf()}`
        )
      }

      // 1. IMMEDIATE UI UPDATE (Optimistic)
      setOptimisticUpdates((prev) =>
        new Map(prev).set(eventId, {
          StartDate: newStartDate.unix().toString(),
          EndDate: newStartDate.add(duration, 'second').unix().toString(),
          Rank: rank,
          AllDay: allDay
        })
      )

      if (currentEvent) {
        Database.write(async () => {
          ;(await localEventByLocalId(currentEvent.id ?? '')).update(
            (local) => {
              local.StartDate = newStartDate.unix().toString()
              local.EndDate = newStartDate
                .add(duration, 'second')
                .unix()
                .toString()
              local.Rank = rank
              local.AllDay = allDay
            }
          )
        }).then(() => {
          // Clear optimistic update after database update
          setOptimisticUpdates((prev) => {
            const newMap = new Map(prev)
            newMap.delete(eventId)
            return newMap
          })

          if (currentEvent._id) {
            updateEventOnServer(
              currentEvent,
              {
                StartDate: newStartDate.unix().toString(),
                EndDate: newStartDate.add(duration, 'second').unix().toString(),
                Rank: rank,
                SupplementaryGroupsIDs: currentEvent.SupplementaryGroupsIDs
              },
              true
            ).catch((error) => {
              console.error('Error updating event on server:', error)

              // Revert optimistic update on error
              setOptimisticUpdates((prev) => {
                const newMap = new Map(prev)
                newMap.delete(eventId)
                return newMap
              })
              Database.write(async () => {
                ;(await localEventById(currentEvent._id ?? '')).update(
                  (local) => {
                    local.StartDate = oldStart
                    local.EndDate = oldEnd
                    local.Rank = oldRank
                    local.AllDay = oldAllday
                  }
                )
              })
            })
          }
        })
      }
    }
    // Memoize rendered dates
    const renderedDates = useMemo(() => {
      return uniqueDates
        .map((date, index) => {
          const eventsForDate = getEventsForDate(date)

          if (eventsForDate.length === 0) return null

          return (
            <Droppable key={date} droppableId={date}>
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{ width: '100%' }}
                >
                  <div>
                    <DateHeader
                      id={date}
                      isFirst={index === 0}
                      isToday={isToday(dayjs(date).toDate())}
                    >
                      <DayHeaderContent
                        date={dayjs(date).toDate()}
                        groupId={props.group?._id}
                        group={props.group}
                        userSettings={props.userSettings}
                        dayNameFormat={props.dayNameFormat}
                        handleAddEvent={props.handleAddEvent}
                      />
                    </DateHeader>
                    {eventsForDate.map((event, eventIndex) => {
                      return (
                        <MemoizedEventItem
                          key={`${event.id ?? event._id}${event.StartDate}${date}${event._rev}`}
                          event={event}
                          eventIndex={eventIndex}
                          date={date}
                          uniqueDates={uniqueDates}
                        />
                      )
                    })}
                  </div>
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          )
        })
        .filter(Boolean)
    }, [
      uniqueDates,
      getEventsForDate,
      props.group,
      props.userSettings,
      props.dayNameFormat,
      props.handleAddEvent
    ])

    useEffect(() => {
      const pendingItem = document.getElementById('pending')

      if (pendingItem) {
        pendingItem?.scrollIntoView({
          behavior: 'instant',
          block: 'start',
          inline: 'nearest'
        })
        return
      }
      const element = document.getElementById(
        calendar.scrollToTime.format('YYYY-MM-DD') ?? ''
      )
      if (element) {
        element.scrollIntoView({
          behavior: 'instant',
          block: 'start',
          inline: 'nearest'
        })
      }
    }, [renderedDates, calendar.scrollToTime])

    return (
      <DragDropContext
        key={'dnd-list'}
        onBeforeDragStart={() => {
          LocalStorage.remove(LocalStorageKey.SHOULD_KEEP_POPUP)
          closePopover()
        }}
        onDragStart={() => {
          LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '1')
          LocalStorage.set(LocalStorageKey.DRAGGING, '1')
        }}
        onDragEnd={onDragEnd}
      >
        <ListViewScrollable
          className="fc-scroller"
          style={{
            transition: 'opacity 0.6s ease-in-out',
            opacity: isProcessingEvents ? 0.5 : 1
          }}
        >
          {eventsForListView.length === 0 && !isProcessingEvents && (
            <NoItems key={0}>{t('nothingOnYourAgenda')}</NoItems>
          )}
          {renderedDates}
          <Box height={80} />
        </ListViewScrollable>
      </DragDropContext>
    )
  },
  (prevProps, nextProps) => {
    // Custom comparison function for React.memo
    return (
      prevProps.events === nextProps.events &&
      prevProps.dayNameFormat === nextProps.dayNameFormat &&
      prevProps.userSettings === nextProps.userSettings &&
      prevProps.group?._id === nextProps.group?._id &&
      prevProps.groupId === nextProps.groupId &&
      prevProps.loadingEvents === nextProps.loadingEvents &&
      prevProps.embedMode === nextProps.embedMode
    )
  }
)

// Create a memoized event item component
const MemoizedEventItem = React.memo(
  ({
    event,
    eventIndex,
    date,
    uniqueDates
  }: {
    event: EventDoc
    eventIndex: number
    date: string
    uniqueDates: string[]
  }) => {
    const [contextMenuPosition, setContextMenuPosition] = React.useState<{
      x: number
      y: number
    } | null>(null)
    const [selectedEventForMenu, setSelectedEventForMenu] = React.useState<{
      event: EventDoc
      instanceStart: number
      instanceEnd: number
    } | null>(null)

    const handleContextMenu = (
      e: React.MouseEvent,
      eventDoc: EventDoc,
      instanceStart: number,
      instanceEnd: number
    ) => {
      e.preventDefault()
      setContextMenuPosition({ x: e.clientX, y: e.clientY })
      setSelectedEventForMenu({
        event: eventDoc,
        instanceStart,
        instanceEnd
      })
    }

    const handleCloseContextMenu = () => {
      setContextMenuPosition(null)
      setSelectedEventForMenu(null)
    }

    const current = dayjs(date)
    const startEvent = dayjs(Number(event.EndDate) * 1000)
    let day =
      eventLengthInDays(event) -
      (startEvent.startOf('day').unix() - current.startOf('day').unix()) / 86400

    if (isConvertedGroupId(event.GroupID) && event.AllDay === '1') {
      day += 1
    }

    const longLabel = isEventMoreThanADay(event)
      ? `, (${t('day_of_long_event', {
          num: day,
          total: eventLengthInDays(event)
        })})`
      : undefined

    let start = event.late
      ? dayjs(Number(event.lateOriginalStartTime) * 1000).format('MMM D YYYY')
      : dateToTimeString(dayjs(Number(event.StartDate) * 1000).toDate(), true)
    let end = event.late
      ? dayjs(Number(event.lateOriginalEndTime) * 1000).format('MMM D YYYY')
      : dateToTimeString(dayjs(Number(event.EndDate) * 1000).toDate(), true)
    const uniqueId = `${event.id ?? event._id}${event.StartDate}${date}`

    if (isEventMoreThanADay(event)) {
      if (
        isSameDay(
          dayjs(date).toDate(),
          dayjs(Number(event.StartDate) * 1000).toDate()
        )
      ) {
        start = dateToTimeString(
          dayjs(Number(event.StartDate) * 1000).toDate(),
          true
        )
        end = dayjs(Number(event.EndDate) * 1000).format('MMM D')
      } else if (
        isSameDay(
          dayjs(date).toDate(),
          dayjs(Number(event.EndDate) * 1000).toDate()
        )
      ) {
        start = dayjs(Number(event.StartDate) * 1000).format('MMM D')
        end = dateToTimeString(
          dayjs(Number(event.EndDate) * 1000).toDate(),
          true
        )
      } else {
        start = dayjs(Number(event.StartDate) * 1000).format('MMM D')
        end = dayjs(Number(event.EndDate) * 1000).format('MMM D')
      }

      if (event.AllDay === '1' && !event.late) {
        start = dayjs(Number(event.StartDate) * 1000).format('MMM D')
        end = dayjs(Number(event.EndDate) * 1000)
          .add(isConvertedGroupId(event.GroupID ?? '') ? -1 : 0, 'day')
          .format('MMM D')
      }
    } else {
      if (event.AllDay === '1' && !event.late) {
        start = t('allDay')
        end = ''
      }
    }

    return (
      <>
        {contextMenuPosition && selectedEventForMenu && (
          <EventContextMenu
            x={contextMenuPosition.x}
            y={contextMenuPosition.y}
            onClose={handleCloseContextMenu}
            event={selectedEventForMenu.event}
            group={undefined}
            instanceStart={selectedEventForMenu.instanceStart}
            instanceEnd={selectedEventForMenu.instanceEnd}
          />
        )}
        <Draggable
          isDragDisabled={
            isGuest() ||
            (event.Recurrence !== undefined &&
              event.Recurrence !== null &&
              typeof event.Recurrence !== 'string')
          }
          key={uniqueId}
          draggableId={uniqueId}
          index={eventIndex}
        >
          {(provided) => {
            return (
              <div
                data-unique-id={uniqueId}
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                onContextMenu={(e) => {
                  if (event._id?.length === 0 || isGuest()) return
                  handleContextMenu(
                    e,
                    event,
                    Number(event.StartDate) * 1000,
                    Number(event.EndDate) * 1000
                  )
                }}
              >
                <EventRow
                  id={event._id?.length === 0 ? 'pending' : undefined}
                  key={`${event._id}${event.StartDate}`}
                >
                  <TimeColumn>
                    <TimeContainer>
                      <TimeLabel
                        sx={{
                          color: event.late ? 'red' : undefined
                        }}
                      >
                        {start}
                      </TimeLabel>
                      {start !== end && !event.late ? (
                        <TimeLabel>{end}</TimeLabel>
                      ) : (
                        <></>
                      )}
                    </TimeContainer>
                  </TimeColumn>
                  <Event
                    alwaysFullSize
                    isStart
                    instanceStart={Number(event.StartDate) * 1000}
                    instanceEnd={
                      (Number(event.EndDate) -
                        (event.AllDay === '1' &&
                        (event._id?.length ?? 0) > 0 &&
                        event.StartDate != event.EndDate &&
                        isConvertedGroupId(event.GroupID ?? '')
                          ? 12 * 60 * 60
                          : 0)) *
                      1000
                    }
                    event={event}
                    title={`${event.Text}${longLabel ? longLabel : ''}`}
                  />
                </EventRow>
              </div>
            )
          }}
        </Draggable>
      </>
    )
  }
)

export default ListView
